import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Transaction } from '../types';
import { formatCurrency } from '../utils/formatting';


interface MonthlyAnalysisViewProps {
    transactions: Transaction[];
    theme: 'light' | 'dark';
    currency: string;
}

const MonthlyAnalysisView: React.FC<MonthlyAnalysisViewProps> = ({ transactions, theme, currency }) => {
    
    const monthlyData = useMemo(() => {
        // Use a Map for better performance and to store the first date object for sorting.
        const dataByMonth = new Map<string, { ingresos: number, gastos: number, date: Date }>();

        transactions.forEach(t => {
            const transactionDate = new Date(t.date);

            // Check for invalid dates and skip the transaction if it is.
            if (isNaN(transactionDate.getTime())) {
                return; 
            }

            const monthKey = transactionDate.toLocaleString('es-ES', { month: 'long', year: 'numeric' });
            
            if (!dataByMonth.has(monthKey)) {
                dataByMonth.set(monthKey, { 
                    ingresos: 0, 
                    gastos: 0,
                    // Store the first valid date object of the month for sorting later.
                    date: new Date(transactionDate.getFullYear(), transactionDate.getMonth(), 1)
                });
            }

            const monthData = dataByMonth.get(monthKey)!;

            if (t.amount > 0) {
                monthData.ingresos += t.amount;
            } else {
                monthData.gastos += Math.abs(t.amount);
            }
        });

        // Convert the map to an array.
        const aggregatedData = Array.from(dataByMonth.entries());

        // Sort based on the stored date object.
        const sortedData = aggregatedData.sort(([, a], [, b]) => a.date.getTime() - b.date.getTime());

        // Map to the final format required by the chart.
        return sortedData.map(([name, values]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            Ingresos: parseFloat(values.ingresos.toFixed(2)),
            Gastos: parseFloat(values.gastos.toFixed(2)),
        }));
        
    }, [transactions]);
    
    const chartColors = {
        tick: theme === 'dark' ? '#9CA3AF' : '#4B5563',
        grid: theme === 'dark' ? '#374151' : '#E5E7EB',
        tooltip: {
            backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
            border: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
            borderRadius: '0.5rem',
        },
        cursor: theme === 'dark' ? '#37415180' : '#E5E7EB80',
    };

    // Sorter to control tooltip item order to show Ingresos before Gastos.
    const tooltipItemSorter = (item: any) => {
        if (item.dataKey === 'Ingresos') return -1;
        if (item.dataKey === 'Gastos') return 1;
        return 0;
    };
    
    const legendItemSorter = (item: any): number => {
        if (item.value === 'Ingresos') return -1;
        if (item.value === 'Gastos') return 1;
        return 0;
    };

    return (
        <div className="space-y-8">
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                <h3 className="font-bold text-lg mb-1 text-light-text-primary dark:text-text-primary">Evolución Mensual</h3>
                <p className="text-sm text-light-text-secondary dark:text-text-secondary mb-6">Visualiza tus ingresos y gastos a lo largo del tiempo.</p>
                
                {monthlyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={monthlyData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                            <XAxis dataKey="name" stroke={chartColors.tick} tick={{ fontSize: 12 }} />
                            <YAxis stroke={chartColors.tick} tickFormatter={(value) => formatCurrency(Number(value), currency, { style: 'decimal' })} />
                            <Tooltip 
                                contentStyle={chartColors.tooltip} 
                                cursor={{ fill: chartColors.cursor }}
                                formatter={(value: number) => formatCurrency(value, currency)}
                                itemSorter={tooltipItemSorter}
                            />
                            <Legend itemSorter={legendItemSorter} />
                            <Bar dataKey="Ingresos" fill="#10B981" name="Ingresos" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="Gastos" fill="#EF4444" name="Gastos" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-[400px] text-light-text-secondary dark:text-text-secondary">
                        No hay suficientes datos para el análisis mensual.
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonthlyAnalysisView;